import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default slides (used when no slides configured in DB)
const defaultSlides = [
  {
    image: 'https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/eza9ca7i_40.jpg',
    title: 'Bodas',
    subtitle: 'Capturando el amor en cada momento',
    description: 'Fotografía de bodas con un enfoque artístico y emotivo que cuenta tu historia de amor.'
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/h15n10o7_07.jpg',
    title: 'Retratos',
    subtitle: 'Tu esencia, inmortalizada',
    description: 'Sesiones de retrato que capturan tu personalidad única con elegancia y naturalidad.'
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/xox15to6__IMG4943-HDR_web.jpg',
    title: 'Naturaleza',
    subtitle: 'La belleza del mundo natural',
    description: 'Paisajes y naturaleza capturados en su máximo esplendor, desde amaneceres hasta atardeceres.'
  }
];

export const Hero = () => {
  const [slides, setSlides] = useState(defaultSlides);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rotationInterval, setRotationInterval] = useState(6000); // Default 6 seconds

  // Fetch slides and config from API
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const response = await fetch(`${API}/hero-slides`);
        if (response.ok) {
          const data = await response.json();
          // New format returns { slides: [], config: {} }
          if (data.slides && data.slides.length > 0) {
            setSlides(data.slides);
          } else if (Array.isArray(data) && data.length > 0) {
            // Backwards compatibility
            setSlides(data);
          }
          // Set rotation interval from config
          if (data.config && data.config.rotation_interval) {
            setRotationInterval(data.config.rotation_interval * 1000);
          }
        }
      } catch (error) {
        console.error('Error fetching hero slides:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSlides();
  }, []);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, rotationInterval);
    return () => clearInterval(timer);
  }, [nextSlide, rotationInterval]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const textVariants = {
    enter: { opacity: 0, y: 30 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -30 }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="inicio" className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Background Images */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={current}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ x: { type: 'spring', stiffness: 300, damping: 30 }, opacity: { duration: 0.4 } }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slides[current].image})` }}
          />
          <div className="absolute inset-0 bg-black/50" />
        </motion.div>
      </AnimatePresence>

      {/* Top gradient overlay for navbar visibility */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/70 to-transparent z-10 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl text-white"
            >
              <span 
                className="text-sm uppercase tracking-widest text-white/70 block mb-4"
              >
                {slides[current].subtitle}
              </span>
              <h1 
                className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight leading-none mb-6"
              >
                {slides[current].title}
              </h1>
              <p 
                className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-lg"
              >
                {slides[current].description}
              </p>
              <div>
                <Button 
                  onClick={scrollToContact}
                  className="rounded-none border border-white bg-transparent text-white hover:bg-white hover:text-black transition-colors duration-300 px-8 py-3 uppercase tracking-widest text-xs font-medium"
                  data-testid="hero-cta"
                >
                  Contactar
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute bottom-1/2 translate-y-1/2 left-4 md:left-8 z-20">
        <button
          onClick={prevSlide}
          className="w-12 h-12 border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          data-testid="hero-prev"
          aria-label="Anterior"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>
      <div className="absolute bottom-1/2 translate-y-1/2 right-4 md:right-8 z-20">
        <button
          onClick={nextSlide}
          className="w-12 h-12 border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          data-testid="hero-next"
          aria-label="Siguiente"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > current ? 1 : -1);
              setCurrent(index);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === current ? 'bg-white w-8' : 'bg-white/40'
            }`}
            data-testid={`hero-dot-${index}`}
            aria-label={`Ir a slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};
